import { useState } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Send } from "lucide-react";
import { api, CONFIG } from "@/lib/api";
import { track } from "@/lib/pixel";
import { Input } from "@/components/ui/input";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_RE = /^[+()\-\s\d]{7,}$/;

/**
 * GlobalEnquiry — a persistent enquiry section rendered above the footer
 * on every page. Captures the three mandatory fields the user requires:
 *   Name, Email, Mobile — all required.
 * On success, POSTs to /api/leads (which triggers the Resend email pipeline)
 * and fires the Meta Pixel Lead event.
 */
export default function GlobalEnquiry() {
  const loc = useLocation();
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [sent, setSent] = useState(false);

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Name is required";
    if (!form.email.trim()) e.email = "Email is required";
    else if (!EMAIL_RE.test(form.email.trim())) e.email = "Enter a valid email";
    if (!form.phone.trim()) e.phone = "Mobile is required";
    else if (!PHONE_RE.test(form.phone.trim())) e.phone = "Enter a valid mobile";
    return e;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    const eObj = validate();
    setErrors(eObj);
    if (Object.keys(eObj).length) return;
    setLoading(true);
    try {
      const source = `global-enquiry${loc.pathname === "/" ? "-home" : loc.pathname}`;
      const { data } = await api.post("/leads", {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        consent: true,
        source,
      });
      track("Lead", {
        source,
        content_name: "global-enquiry",
        page: loc.pathname,
        currency: "INR",
      });
      // Google Ads — conversion event on successful lead form submission
      if (typeof window !== "undefined" && typeof window.gtag === "function") {
        window.gtag("event", "conversion", {
          send_to: "AW-16567701217/rzPHCIHHno4dEOGdjNw9",
        });
      }
      toast.success(
        data.message || "Thank you. Our team will contact you shortly."
      );
      setForm({ name: "", email: "", phone: "" });
      setSent(true);
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="global-enquiry"
      data-testid="global-enquiry"
      className="relative border-t border-[color:var(--pcv-border)]"
      style={{
        background:
          "linear-gradient(180deg, var(--pcv-surface) 0%, var(--pcv-bg) 100%)",
      }}
    >
      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-14 lg:py-20">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          {/* Left — pitch */}
          <div className="lg:col-span-5">
            <div className="eyebrow mb-4" style={{ color: "var(--pcv-gold)" }}>
              Enquire Now
            </div>
            <h2 className="font-serif-lux text-3xl sm:text-4xl lg:text-5xl leading-[1.1] tracking-tight">
              Interested in{" "}
              <span style={{ color: "var(--pcv-gold)" }}>
                Provident Winworth?
              </span>
            </h2>
            <p className="mt-5 text-[color:var(--pcv-text-2)] text-sm sm:text-base leading-relaxed max-w-md">
              Share your details and our sales team will reach out with the
              confidential price sheet, floor plans, and a private site-visit
              slot.
            </p>
            <div className="mt-6 flex flex-wrap items-center gap-3 text-[11px] uppercase tracking-[0.18em] text-[color:var(--pcv-text-2)]">
              <span>Direct callback</span>
              <span className="opacity-30">·</span>
              <span>Confidential</span>
              <span className="opacity-30">·</span>
              <span>Zero spam</span>
            </div>
            <div className="mt-8 pt-6 border-t border-[color:var(--pcv-border)]/60 flex items-center gap-4">
              <a
                href={`tel:${CONFIG.phoneRaw}`}
                className="text-sm hover:opacity-80 transition-opacity"
                style={{ color: "var(--pcv-gold)" }}
                data-testid="global-enquiry-call"
              >
                Call {CONFIG.phone}
              </a>
              <span className="opacity-30">·</span>
              <a
                href={`https://wa.me/${CONFIG.whatsapp}`}
                target="_blank"
                rel="noreferrer"
                className="text-sm hover:opacity-80 transition-opacity"
                style={{ color: "var(--pcv-wa)" }}
                data-testid="global-enquiry-whatsapp"
              >
                WhatsApp us
              </a>
            </div>
          </div>

          {/* Right — form */}
          <div className="lg:col-span-7">
            <form
              onSubmit={submit}
              noValidate
              data-testid="global-enquiry-form"
              className="glass border border-[color:var(--pcv-border)] p-6 sm:p-8 lg:p-10 relative overflow-hidden"
            >
              <div
                aria-hidden
                className="absolute -top-16 -right-16 w-56 h-56 rounded-full opacity-20 blur-3xl pointer-events-none"
                style={{ background: "var(--pcv-gold)" }}
              />
              <div className="grid sm:grid-cols-2 gap-4 relative">
                <Field
                  label="Full Name"
                  required
                  error={errors.name}
                  testid="global-enquiry-name"
                >
                  <Input
                    value={form.name}
                    onChange={(e) =>
                      setForm({ ...form, name: e.target.value })
                    }
                    placeholder="Your name"
                    data-testid="global-enquiry-name-input"
                    className="bg-transparent border-[color:var(--pcv-border)] text-[color:var(--pcv-text)] placeholder:text-[color:var(--pcv-text-2)]/60 h-12"
                    aria-invalid={!!errors.name}
                    aria-required="true"
                  />
                </Field>
                <Field
                  label="Mobile Number"
                  required
                  error={errors.phone}
                  testid="global-enquiry-phone"
                >
                  <Input
                    inputMode="tel"
                    value={form.phone}
                    onChange={(e) =>
                      setForm({ ...form, phone: e.target.value })
                    }
                    placeholder="+91 XXXXX XXXXX"
                    data-testid="global-enquiry-phone-input"
                    className="bg-transparent border-[color:var(--pcv-border)] text-[color:var(--pcv-text)] placeholder:text-[color:var(--pcv-text-2)]/60 h-12"
                    aria-invalid={!!errors.phone}
                    aria-required="true"
                  />
                </Field>
                <Field
                  label="Email"
                  required
                  error={errors.email}
                  testid="global-enquiry-email"
                  className="sm:col-span-2"
                >
                  <Input
                    type="email"
                    value={form.email}
                    onChange={(e) =>
                      setForm({ ...form, email: e.target.value })
                    }
                    placeholder="you@example.com"
                    data-testid="global-enquiry-email-input"
                    className="bg-transparent border-[color:var(--pcv-border)] text-[color:var(--pcv-text)] placeholder:text-[color:var(--pcv-text-2)]/60 h-12"
                    aria-invalid={!!errors.email}
                    aria-required="true"
                  />
                </Field>
              </div>

              <button
                type="submit"
                disabled={loading}
                data-testid="global-enquiry-submit"
                className="btn-gold w-full mt-6 h-12 justify-center disabled:opacity-60 disabled:cursor-wait inline-flex items-center gap-2 relative"
              >
                <Send className="w-4 h-4" />
                {loading
                  ? "Sending…"
                  : sent
                  ? "Send Another Enquiry"
                  : "Send My Enquiry"}
              </button>

              <p className="text-[10px] text-center text-[color:var(--pcv-text-2)]/60 mt-3">
                By submitting you agree to be contacted by our sales team via
                phone / WhatsApp / email. 100% confidential.
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}

function Field({ label, required, error, testid, className = "", children }) {
  return (
    <div className={className}>
      <label
        htmlFor={testid}
        className="text-[10px] uppercase tracking-[0.22em] text-[color:var(--pcv-text-2)] mb-1.5 flex items-center gap-1"
      >
        {label}
        {required && (
          <span style={{ color: "var(--pcv-gold)" }} aria-hidden>
            *
          </span>
        )}
      </label>
      {children}
      {error && (
        <p
          className="text-[11px] mt-1.5"
          style={{ color: "var(--pcv-danger)" }}
          data-testid={`${testid}-error`}
          role="alert"
        >
          {error}
        </p>
      )}
    </div>
  );
}
